import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ActivitySummaryMetric,
  GetVolunteerActivitySummaryResponse,
  VolunteerActivitySummary,
} from '@amzn/red-velvet-api';
import { useTimezone } from '../timezone';
import { getEnabledFeatures, useFeatures } from '../features';
import { getClient } from '../../utils/getClient';
import { saveHadActivitySummary } from '../../utils/activitySummaryStorage';
import { publishKatalMetric } from '../../components/katalAnalytics';
import {
  ACTIVITY_SUMMARY_MOCK_PARAM,
  areActivitySummaryMocksAllowed,
  getVolunteerActivitySummaryMock,
} from '../../components/activitySummary/mocks/volunteerActivitySummaryFixtures';

export const VOLUNTEER_ACTIVITY_SUMMARY_QUERY_KEY = ['RedVelvetApi', 'getVolunteerActivitySummary'];

/** Names this panel in its Katal events and its saved "had a summary" value. */
export const VOLUNTEER_SUMMARY_PANEL = 'volunteer';

export const ACTIVITY_SUMMARY_LOAD_ERROR_METRIC = 'activitySummaryLoadError';
export const ACTIVITY_SUMMARY_PARTIAL_METRIC = 'activitySummaryPartial';

// Listed by hand because the shared enum also carries metrics this panel never shows.
const VOLUNTEER_METRICS = [
  ActivitySummaryMetric.VOLUNTEERING_SINCE,
  ActivitySummaryMetric.SHIFTS_COMPLETED,
  ActivitySummaryMetric.VOLUNTEER_MINUTES,
  ActivitySummaryMetric.ATTENDANCE_TRACKING,
];

const unavailableVolunteerMetrics = (summary: VolunteerActivitySummary) =>
  VOLUNTEER_METRICS.filter((metric) => summary.unavailableMetrics.includes(metric));

/** Every number failed, so the panel hides just as it does when the request fails. */
const isEveryMetricUnavailable = (summary: VolunteerActivitySummary | undefined) =>
  summary !== undefined && unavailableVolunteerMetrics(summary).length === VOLUNTEER_METRICS.length;

/**
 * Amazonians never see an error on the panel, so these Katal events are the only way to tell from
 * the browser that some or all of their numbers are missing.
 */
function reportUnavailableMetrics(summary: VolunteerActivitySummary | undefined) {
  if (!summary) return;
  const unavailable = unavailableVolunteerMetrics(summary);
  if (unavailable.length === VOLUNTEER_METRICS.length) {
    publishKatalMetric(ACTIVITY_SUMMARY_LOAD_ERROR_METRIC, VOLUNTEER_SUMMARY_PANEL);
  } else if (unavailable.length > 0) {
    publishKatalMetric(ACTIVITY_SUMMARY_PARTIAL_METRIC, VOLUNTEER_SUMMARY_PANEL);
  }
}

/**
 * The caller's own Volunteer summary, for "this year" in their browser's time zone.
 *
 * Adding `?activitySummaryMock=<fixture>` swaps in a mock fixture instead of calling the API, to
 * review edge and failure states. That only works on local, dev, alpha, ITL targets, and personal
 * stacks; beta and prod always call the API.
 */
export function useVolunteerActivitySummary() {
  const timeZone = useTimezone();
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));
  const mockFixture = areActivitySummaryMocksAllowed()
    ? searchParams.get(ACTIVITY_SUMMARY_MOCK_PARAM)
    : null;

  const fetchSummary = async () =>
    mockFixture !== null
      ? getVolunteerActivitySummaryMock(mockFixture)
      : client.getVolunteerActivitySummary({ timeZone });

  return useQuery<GetVolunteerActivitySummaryResponse>({
    queryKey: [...VOLUNTEER_ACTIVITY_SUMMARY_QUERY_KEY, { timeZone, mockFixture }],
    queryFn: async () => {
      const response = await fetchSummary().catch((error: unknown) => {
        publishKatalMetric(ACTIVITY_SUMMARY_LOAD_ERROR_METRIC, VOLUNTEER_SUMMARY_PANEL);
        throw error;
      });
      reportUnavailableMetrics(response.summary);
      // A failed load leaves the saved value as it was, since it says nothing about the Amazonian.
      // One where every number failed counts as failed: saving true would show the placeholder next
      // time and then pop the panel out again if the numbers still fail.
      if (!isEveryMetricUnavailable(response.summary)) {
        saveHadActivitySummary(VOLUNTEER_SUMMARY_PANEL, response.summary !== undefined);
      }
      return response;
    },
    // The panel hides on failure and never shows an error, so retrying only delays that.
    retry: false,
  });
}
